import {
  ColumnDefinition,
  renderColumn,
  sortableColumn
} from 'components/TableColumn/TableColumn';
import { useCallback, useEffect, useMemo, useState } from 'react';
import formatters, {
  getRtbCampaignGroupDeliveryDesDatas,
  getRtbCampaignGroupStatusDesData
} from './listFormatters';
import styles from './l1ObjectList.module.scss';
import {
  formatPrice,
  getCurrencyLabel,
  getPriceValue
} from 'helper/CurrencyHelper';
import { useRouteMatch } from 'react-router-dom';
import i18n from 'i18n';
import _ from 'lodash';
import {
  DefaultL1ObjectManager,
  L1ObjectManager
} from 'core/l1Object/L1ObjectManager';
import { numberWithCommas } from 'utils/StringUtil';
import { AddonFeatureManager } from 'core';
import { ADDONFEATURE } from 'core/agency/AddonFeature';
import { Order, State } from 'core/order/Order';
import {
  AdvertiserManager,
  DefaultAdvertiserManager
} from 'core/advertiser/AdvertiserManager';
import moment from 'moment';
import { useCallAPI } from 'hooks/useCallAPI';
import {
  getCpa,
  getCpc,
  getCtr,
  getCvr,
  getVctr,
  getViewRate,
  getDivideValue,
  getPercentValue,
  metricsAdder,
  metricsFormatter
} from 'helper/MetricsHelper';
import { FilterMenuTabConfig } from 'components/common/FilterMenuTab/FilterMenuTab';
import {
  L1Object,
  L1ObjectState,
  L2ObjectRunningStatus
} from 'core/l1Object/L1Object';
import { CampaignGroupAuditLogModel } from 'containers/AuditLog/CampaignGroupAuditLogModel';
import { useCoreContext } from 'contexts/coreContext';
import { notSettleOrder } from 'core/permission/PermissionDSL';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';

export enum L1ObjectListColumns {
  ID = 'l1ObjectId',
  BUDGET = 'budget',
  CHANNEL = 'channel',
  AUTO_OPTIMISE = 'autoOptimise',
  STATE = 'state',
  DELIVERY = 'delivery',
  RESULTS = 'results',
  IMPRES = 'impres',
  VIEWABLE = 'viewable',
  CLICKS = 'clicks',
  CPC = 'cpc',
  CTR = 'ctr',
  VCTR = 'vctr',
  CONVERT = 'convs',
  CPA = 'cpa',
  CVR = 'cvr',
  UU = 'uu',
  VIEW = 'view',
  VIEW_RATE = 'viewRate',
  VIEWABLE_VIEW_RATE = 'viewableViewRate',
  SPENT = 'spent',
  BASIC_SPENT = 'basicSpent',
  EDITBTNS = 'editBtns'
}

export enum ListChannel {
  ALL = 'ALL',
  RTB = 'RTB',
  PIC = 'PIC',
  FRIDGE = 'FRIDGE',
  HWACOM = 'HWACOM'
}

export enum L1ObjectListType {
  BASIC = 'basic',
  PERFORMANCE = 'performance',
  RTB_VIDEO_PERFORMANCE = 'rtb_video_performance',
  AUDIT_LOG = 'auditLog'
}

export type L1ObjectListState = {
  readonly searchString: string;
  readonly filteredList: any[];
  readonly deleteL1Object?: () => void;
  readonly selectedMetricsGroup: L1ObjectListType;
  readonly summaryData: any;
  readonly selectedChannel: string;
  readonly selectedStatusFilter: string[];
  readonly selectedDeliveryFilter: string[];
  readonly l1ObjectStatusList: string[];
  readonly l1ObjectDeliveryList: string[];
};

const defaultL1ObjectManager: L1ObjectManager = new DefaultL1ObjectManager();

const channelMetricsGroupMap = {
  [ListChannel.ALL]: [L1ObjectListType.BASIC, L1ObjectListType.PERFORMANCE],
  [ListChannel.RTB]: [
    L1ObjectListType.BASIC,
    L1ObjectListType.PERFORMANCE,
    L1ObjectListType.RTB_VIDEO_PERFORMANCE,
    L1ObjectListType.AUDIT_LOG
  ],
  [ListChannel.PIC]: [
    L1ObjectListType.BASIC,
    L1ObjectListType.PERFORMANCE,
    L1ObjectListType.AUDIT_LOG
  ],
  [ListChannel.FRIDGE]: [
    L1ObjectListType.BASIC,
    L1ObjectListType.PERFORMANCE,
    L1ObjectListType.AUDIT_LOG
  ],
  [ListChannel.HWACOM]: [
    L1ObjectListType.BASIC,
    L1ObjectListType.PERFORMANCE,
    L1ObjectListType.AUDIT_LOG
  ]
};

const defaultAdvertiserManager: AdvertiserManager =
  new DefaultAdvertiserManager();

export const useL1ObjectListModel = (
  order: Order,
  l1ObjectList: any[],
  refreshList: () => void,
  addonFeatureManager: AddonFeatureManager,
  l1ObjectManager: L1ObjectManager = defaultL1ObjectManager,
  advertiserManager: AdvertiserManager = defaultAdvertiserManager
) => {
  const [state, setState] = useState({
    searchString: '',
    filteredList: [],
    selectedMetricsGroup: L1ObjectListType.BASIC,
    selectedChannel: ListChannel.ALL,
    summaryData: undefined,
    selectedStatusFilter: [],
    selectedDeliveryFilter: [],
    l1ObjectStatusList: [],
    l1ObjectDeliveryList: []
  } as L1ObjectListState);

  const { loading, callAPIs } = useCallAPI();

  const core = useCoreContext();
  const isAdmin = _.isNil(_.get(core, 'accountManager.localeMeta.agencyId'));

  const l1ObjectWithPerformanceList = useMemo(() => {
    return l1ObjectList.map((l1Object) => {
      const report = l1Object.report;
      const budget = {
        lifetime: _.defaultTo(l1Object.budget, -1),
        daily: _.defaultTo(l1Object.dailyBudget, -1)
      };
      const conversionList = _.defaultTo(l1Object.conversion, []);
      const conversion = _.mapValues(
        _.keyBy(
          conversionList.map((data) => ({
            ...data,
            action_type: data.action_type.replace('.', '_')
          })),
          'action_type'
        ),
        'actions'
      );
      const impres = _.get(report, 'impres', _.get(report, 'impressions'));
      const spent = _.get(report, 'spent', _.get(report, 'spend', 0));
      setState((state) => ({
        ...state,
        l1ObjectStatusList: _.uniq(
          _.concat(
            state.l1ObjectStatusList,
            getFilterTypeDes(L1ObjectListColumns.STATE, l1Object, order)
          )
        ),
        l1ObjectDeliveryList: _.uniq(
          _.concat(
            state.l1ObjectDeliveryList,
            getFilterTypeDes(L1ObjectListColumns.DELIVERY, l1Object, order)
          )
        )
      }));
      const view = _.get(report, 'view', 0);
      const viewable = _.get(report, 'viewable', 0);
      return report
        ? {
            ..._.omit(l1Object, ['report', 'conversion']),
            ...report,
            ...conversion,
            budget,
            [L1ObjectListColumns.IMPRES]: impres,
            [L1ObjectListColumns.CPA]: getPriceValue(
              order.currency,
              getCpa(spent, report.convs)
            ),
            [L1ObjectListColumns.CPC]: getPriceValue(
              order.currency,
              getCpc(spent, report.clicks)
            ),
            [L1ObjectListColumns.CTR]: getCtr(impres, report.clicks),
            [L1ObjectListColumns.CVR]: getCvr(report.convs, report.clicks),
            [L1ObjectListColumns.VCTR]: getVctr(report.viewable, report.clicks),
            [L1ObjectListColumns.SPENT]: spent,
            // [POS-4998] l1Object list uu use lifetime uu
            [L1ObjectListColumns.UU]: _.get(
              report,
              'lifetimeUU',
              _.get(report, 'lifetimeReach', 0)
            ),
            [L1ObjectListColumns.VIEW_RATE]: getViewRate(view, impres),
            [L1ObjectListColumns.VIEWABLE_VIEW_RATE]: getViewRate(
              view,
              viewable
            )
          }
        : {
            ...l1Object,
            ...conversion,
            budget
          };
    });
  }, [order, l1ObjectList]);

  const allowedChannels = useMemo(() => {
    return l1ObjectManager.getAllowedChannels(addonFeatureManager);
  }, [l1ObjectManager, addonFeatureManager]);

  const channels = useMemo(() => {
    const channelsOfCurrentList = l1ObjectList.reduce((acc, l1Object) => {
      return _.uniq([...acc, l1Object.channel]);
    }, []);

    const allChannels = [
      ListChannel.ALL,
      ...ChannelGroupSetting.values().map((setting) => setting.key)
    ];

    return allChannels.filter((channel) => {
      return (
        channel === ListChannel.ALL ||
        channelsOfCurrentList.includes(channel) ||
        allowedChannels.includes(channel)
      );
    });
  }, [allowedChannels, l1ObjectList]);

  const filterMenuTabConfigs: FilterMenuTabConfig[] = useMemo(() => {
    const handleOnFilterApply = (filterType: string, filterList: string[]) => {
      setState({
        ...state,
        [filterType]: [...filterList]
      });
    };

    return [
      {
        filterType: i18n.t('l1ObjectList.labels.statusFilter'),
        menuTitle: i18n.t('l1ObjectList.labels.statusFilterMenuTitle'),
        tag: i18n.t('l1ObjectList.labels.statusTag'),
        selectedValues: state.selectedStatusFilter,
        options: state.l1ObjectStatusList,
        applyMethod: _.partial(handleOnFilterApply, 'selectedStatusFilter')
      },
      {
        filterType: i18n.t('l1ObjectList.labels.deliveryFilter'),
        menuTitle: i18n.t('l1ObjectList.labels.deliveryFilterMenuTitle'),
        tag: i18n.t('l1ObjectList.labels.deliveryTag'),
        selectedValues: state.selectedDeliveryFilter,
        options: state.l1ObjectDeliveryList,
        applyMethod: _.partial(handleOnFilterApply, 'selectedDeliveryFilter')
      }
    ];
  }, [state]);

  const match = useRouteMatch();
  const [selectedL1Objects, setSelectedL1Objects] = useState<
    (number | string)[]
  >([]);
  const [advertiserName, setAdvertiserName] = useState('');

  const getAdvertiserOptions = useCallback(async () => {
    callAPIs(
      [
        advertiserManager.getAdvertiserOptions.bind(
          advertiserManager,
          order.agencyId
        )
      ],
      (options) => {
        const advOption = options.find(
          (option) => option.value === order.advertiserId
        );
        setAdvertiserName(_.get(advOption, 'label', ''));
      }
    );
  }, [order.agencyId, order.advertiserId, advertiserManager, callAPIs]);

  useEffect(() => {
    getAdvertiserOptions();
  }, [getAdvertiserOptions]);

  useEffect(() => {
    const filteredList = l1ObjectWithPerformanceList.filter((l1Object) => {
      return l1ObjectListFilter(
        l1Object,
        state.searchString,
        state.selectedChannel,
        order,
        state.selectedStatusFilter,
        state.selectedDeliveryFilter
      );
    });

    const viewableSum = filteredList.reduce<number>(
      (partial, l1Object) => metricsAdder(partial, l1Object.viewable),
      0
    );
    const clickSum = filteredList.reduce<number>(
      (partial, l1Object) => metricsAdder(partial, l1Object.clicks),
      0
    );
    const convsSum = filteredList.reduce<number>(
      (partial, l1Object) => metricsAdder(partial, l1Object.convs),
      0
    );
    const spentSum = filteredList.reduce<number>(
      (partial, l1Object) =>
        metricsAdder(partial, getPriceValue(order.currency, l1Object.spent)),
      0
    );
    const impresSum = filteredList.reduce<number>(
      (partial, l1Object) => metricsAdder(partial, l1Object.impres),
      0
    );
    const viewSum = filteredList.reduce<number>(
      (partial, l1Object) => metricsAdder(partial, l1Object.view),
      0
    );
    const summaryData = {
      [L1ObjectListColumns.ID]: i18n.t('l1ObjectList.labels.l1ObjectCount', {
        count: filteredList.length
      }),
      [L1ObjectListColumns.IMPRES]: numberWithCommas(impresSum),
      [L1ObjectListColumns.VIEWABLE]: numberWithCommas(viewableSum),
      [L1ObjectListColumns.CLICKS]: numberWithCommas(clickSum),
      [L1ObjectListColumns.CONVERT]: numberWithCommas(convsSum),
      [L1ObjectListColumns.VIEW]: numberWithCommas(viewSum),
      [L1ObjectListColumns.CPC]: formatPrice(
        order.currency,
        getDivideValue(spentSum, clickSum)
      ),
      [L1ObjectListColumns.CTR]: getPercentValue(clickSum, impresSum),
      [L1ObjectListColumns.VCTR]: getPercentValue(clickSum, viewableSum),
      [L1ObjectListColumns.CPA]: formatPrice(
        order.currency,
        getDivideValue(spentSum, convsSum)
      ),
      [L1ObjectListColumns.CVR]: getPercentValue(convsSum, clickSum),
      [L1ObjectListColumns.SPENT]: numberWithCommas(spentSum),
      [L1ObjectListColumns.VIEW_RATE]: `${getViewRate(viewSum, impresSum).toFixed(2)}%`,
      [L1ObjectListColumns.VIEWABLE_VIEW_RATE]: `${getViewRate(viewSum, viewableSum).toFixed(2)}%`
    };
    setState((prevState) => ({ ...prevState, filteredList, summaryData }));
  }, [
    state.selectedChannel,
    state.searchString,
    state.selectedStatusFilter,
    state.selectedDeliveryFilter,
    l1ObjectWithPerformanceList,
    order
  ]);

  const onDeleteBtnClick = (l1ObjectToDelete: number) =>
    setState({
      ...state,
      deleteL1Object: _.partial(deleteL1Object, l1ObjectToDelete)
    });

  const onDeleteModalClose = useCallback(
    (refresh: boolean) => {
      setState((prevState) => ({ ...prevState, deleteL1Object: undefined }));
      refresh && refreshList();
    },
    [refreshList]
  );

  const deleteL1Object = useCallback(
    async (l1ObjectId: number) => {
      callAPIs(
        [l1ObjectManager.deleteL1Object.bind(l1ObjectManager, l1ObjectId)],
        () => {
          onDeleteModalClose(true);
        }
      );
    },
    [l1ObjectManager, onDeleteModalClose, callAPIs]
  );

  const onHandleSearch = (searchString: string) =>
    setState({ ...state, searchString });

  const columnDefinition = (
    columnName,
    customLabel?: string,
    sortFunc?: any
  ): ColumnDefinition => ({
    ...sortableColumn(
      columnName,
      _.defaultTo(customLabel, `l1Object.labels.${columnName}`),
      true
    ),
    classes: () => styles[columnName],
    headerClasses: () => styles[columnName],
    sortFunc
  });

  const onSelect = (l1ObjectId: number | string) => {
    setSelectedL1Objects(handleOnSelect(selectedL1Objects, l1ObjectId));
  };

  const editableL1Objects = useMemo(() => {
    return state.filteredList.filter(
      (l1Object) =>
        l1ObjectManager.isChannelAllowed(
          l1Object.channel,
          addonFeatureManager
        ) &&
        l1Object.state !== L1ObjectState.DELETE &&
        l1Object.actionable
    );
  }, [state.filteredList, l1ObjectManager, addonFeatureManager]);

  const actionable = useMemo(() => {
    return state.filteredList
      .filter(
        (l1Object) =>
          selectedL1Objects.indexOf(l1Object.l1ObjectId.toString()) > -1
      )
      .every((l1Object) => l1Object.actionable);
  }, [state.filteredList, selectedL1Objects]);

  const onSelectAll = () => {
    setSelectedL1Objects(
      handleOnSelectAll(selectedL1Objects, editableL1Objects)
    );
  };

  const handleRemoveSelect = () => {
    setSelectedL1Objects([]);
  };

  const onMetricsGroupChange = (selectedMetricsGroup) => {
    if (selectedMetricsGroup === L1ObjectListType.AUDIT_LOG) {
      setSelectedL1Objects([]);
    }
    setState((prevState) => ({ ...prevState, selectedMetricsGroup }));
  };

  const onListChannelChange = (listChannel) => {
    if (listChannel === null) {
      return;
    }
    setSelectedL1Objects([]);
    setState((prevState) => ({ ...prevState, selectedChannel: listChannel }));
  };

  const idColumn = renderColumn(
    {
      ...columnDefinition(L1ObjectListColumns.ID),
      formatExtraData: {
        permissionAwareGetter: (l1Object) =>
          l1ObjectManager
            .getModifyPermissionItem(l1Object)
            .and(notSettleOrder(order)),
        currentUrl: match.url,
        selectedL1Objects,
        onSelect
      }
    },
    formatters.nameFormatter,
    _.partial(
      formatters.nameHeaderFormatter,
      editableL1Objects.length,
      selectedL1Objects,
      order,
      onSelectAll
    )
  );

  const currencyLabel = useMemo(() => {
    return getCurrencyLabel(order.currency);
  }, [order.currency]);

  const budgetColumn = renderColumn(
    columnDefinition(
      L1ObjectListColumns.BUDGET,
      i18n.t(`l1Object.labels.${L1ObjectListColumns.BUDGET}`) +
        ` (${currencyLabel})`,
      budgetSorter
    ),
    _.partial(formatters.budgetFormatter, getBudgetValue, order.currency)
  );

  const basicSpentColumn = renderColumn(
    columnDefinition(
      L1ObjectListColumns.BASIC_SPENT,
      i18n.t(`l1Object.labels.${L1ObjectListColumns.SPENT}`) +
        ` (${currencyLabel})`
    ),
    (_1, l1Object: L1Object) =>
      formatPrice(order.currency, _.get(l1Object, 'spent', 0))
  );

  const editBtns = renderColumn(
    {
      ...columnDefinition(L1ObjectListColumns.EDITBTNS),
      text: '',
      sort: false,
      formatExtraData: {
        permissionAwareGetter: (l1Object) =>
          l1ObjectManager
            .getModifyPermissionItem(l1Object)
            .and(notSettleOrder(order)),
        currentUrl: match.url,
        onDeleteBtnClick
      }
    },
    formatters.floatingEditBtnsFormatter
  );

  const basicColumns = _.compact([
    idColumn,
    renderColumn(
      {
        ...columnDefinition(L1ObjectListColumns.STATE),
        formatExtraData: {
          order
        }
      },
      formatters.stateFormatter
    ),
    renderColumn(
      {
        ...columnDefinition(L1ObjectListColumns.DELIVERY),
        formatExtraData: (l1Object: L1Object) => getL1ObjectProgress(l1Object)
      },
      formatters.deliveryFormatter
    ),
    budgetColumn,
    basicSpentColumn,
    state.selectedChannel === ListChannel.ALL
      ? renderColumn(
          columnDefinition(L1ObjectListColumns.CHANNEL),
          formatters.channelFormatter
        )
      : undefined,
    editBtns
  ]);

  const metricsWithCommasFormatter = _.partial(
    metricsFormatter,
    numberWithCommas
  );

  const spentColumn = renderColumn(
    columnDefinition(
      L1ObjectListColumns.SPENT,
      i18n.t(`l1Object.labels.${L1ObjectListColumns.SPENT}`) +
        ` (${currencyLabel})`
    ),
    _.partial(metricsFormatter, _.partial(formatPrice, order.currency))
  );

  const impresColumn = renderColumn(
    columnDefinition(L1ObjectListColumns.IMPRES),
    metricsWithCommasFormatter
  );

  const viewableEnable = addonFeatureManager.isFeatureEnable(
    ADDONFEATURE.REPORT.REPORT_VIEWABLE_CTR
  );
  const convsEnable = addonFeatureManager.isFeatureEnable(
    ADDONFEATURE.CONVERSION_TRACKING.CONV_TRACKING_LIST
  );
  let viewableColumn: any = undefined;
  let vctrColumn: any = undefined;
  let viewableViewRateColumn: any = undefined;
  if (viewableEnable) {
    viewableColumn = renderColumn(
      columnDefinition(L1ObjectListColumns.VIEWABLE),
      metricsWithCommasFormatter
    );
    vctrColumn = renderColumn(
      columnDefinition(L1ObjectListColumns.VCTR),
      _.partial(metricsFormatter, (value) => `${value.toFixed(2)}%`)
    );
    viewableViewRateColumn = renderColumn(
      columnDefinition(L1ObjectListColumns.VIEWABLE_VIEW_RATE),
      _.partial(metricsFormatter, (value) => `${value.toFixed(2)}%`)
    );
  }

  let convertColumn: any = undefined;
  let cpaColumn: any = undefined;
  let cvrColumn: any = undefined;
  if (convsEnable) {
    convertColumn = renderColumn(
      columnDefinition(L1ObjectListColumns.CONVERT),
      metricsWithCommasFormatter
    );
    cpaColumn = renderColumn(
      columnDefinition(
        L1ObjectListColumns.CPA,
        i18n.t(`l1Object.labels.${L1ObjectListColumns.CPA}`) +
          ` (${currencyLabel})`
      ),
      _.partial(metricsFormatter, _.partial(formatPrice, order.currency))
    );
    cvrColumn = renderColumn(
      columnDefinition(L1ObjectListColumns.CVR),
      _.partial(metricsFormatter, (value) => `${value.toFixed(2)}%`)
    );
  }

  const performanceColumns = _.compact([
    idColumn,
    state.selectedChannel === ListChannel.ALL
      ? renderColumn(
          columnDefinition(L1ObjectListColumns.CHANNEL),
          formatters.channelFormatter
        )
      : undefined,
    budgetColumn,
    renderColumn(
      columnDefinition(L1ObjectListColumns.RESULTS),
      formatters.resultsFormatter
    ),
    impresColumn,
    viewableColumn,
    renderColumn(
      columnDefinition(L1ObjectListColumns.CLICKS),
      metricsWithCommasFormatter
    ),
    renderColumn(
      columnDefinition(
        L1ObjectListColumns.CPC,
        i18n.t(`l1Object.labels.${L1ObjectListColumns.CPC}`) +
          ` (${currencyLabel})`
      ),
      _.partial(metricsFormatter, _.partial(formatPrice, order.currency))
    ),
    renderColumn(
      columnDefinition(L1ObjectListColumns.CTR),
      _.partial(metricsFormatter, (value) => `${value.toFixed(2)}%`)
    ),
    vctrColumn,
    convertColumn,
    cpaColumn,
    cvrColumn,
    renderColumn(
      columnDefinition(L1ObjectListColumns.UU),
      metricsWithCommasFormatter
    ),
    spentColumn,
    editBtns
  ]);

  const rtbVideoPerformanceColumns = _.compact([
    idColumn,
    impresColumn,
    viewableColumn,
    spentColumn,
    renderColumn(
      columnDefinition(L1ObjectListColumns.VIEW),
      metricsWithCommasFormatter
    ),
    renderColumn(
      columnDefinition(L1ObjectListColumns.VIEW_RATE),
      _.partial(metricsFormatter, (value) => `${value.toFixed(2)}%`)
    ),
    viewableViewRateColumn,
    editBtns
  ]);

  const typeColumnsMap = {
    [L1ObjectListType.BASIC]: basicColumns,
    [L1ObjectListType.PERFORMANCE]: performanceColumns,
    [L1ObjectListType.RTB_VIDEO_PERFORMANCE]: rtbVideoPerformanceColumns
  };

  const metricsGroupOptions = useMemo(() => {
    const metricsGroups = channelMetricsGroupMap[state.selectedChannel] || [
      L1ObjectListType.BASIC,
      L1ObjectListType.PERFORMANCE,
      L1ObjectListType.AUDIT_LOG
    ];

    return metricsGroups.map((metricsGroup) => ({
      label: i18n.t(`campaignList.tabs.${metricsGroup}`),
      value: metricsGroup
    }));
  }, [state.selectedChannel]);

  useEffect(() => {
    if (
      metricsGroupOptions.find((option) => {
        return option.value === state.selectedMetricsGroup;
      }) === undefined
    ) {
      setState((prevState) => ({
        ...prevState,
        selectedMetricsGroup: metricsGroupOptions[0].value
      }));
    }
  }, [state.selectedMetricsGroup, metricsGroupOptions]);

  const activeL1Object = async () => {
    callAPIs(
      [
        l1ObjectManager.updateL1ObjectState.bind(
          l1ObjectManager,
          selectedL1Objects,
          'activate'
        )
      ],
      () => {
        setSelectedL1Objects([]);
        refreshList();
      }
    );
  };

  const deactiveL1Object = async () => {
    callAPIs(
      [
        l1ObjectManager.updateL1ObjectState.bind(
          l1ObjectManager,
          selectedL1Objects,
          'deactivate'
        )
      ],
      () => {
        setSelectedL1Objects([]);
        refreshList();
      }
    );
  };

  const auditLogModel = useMemo(() => {
    const channelL1ObjectList = l1ObjectList.filter(
      (l1Object) =>
        l1Object.channel === state.selectedChannel &&
        l1Object.state !== L1ObjectState.DELETE
    );
    return new CampaignGroupAuditLogModel(
      order.id,
      isAdmin,
      channelL1ObjectList
    );
  }, [order.id, isAdmin, l1ObjectList, state.selectedChannel]);

  return {
    state,
    columns: typeColumnsMap[state.selectedMetricsGroup],
    loading,
    currentUrl: match.url,
    selectedL1Objects,
    actionable,
    metricsGroupOptions,
    canNotCreateMessage: getCanNotCreateMessage(order, allowedChannels),
    filterMenuTabConfigs,
    advertiserName,
    channels,
    auditLogModel,
    onHandleSearch,
    onMetricsGroupChange,
    onListChannelChange,
    onDeleteModalClose: _.partial(onDeleteModalClose, false),
    handleRemoveSelect,
    activeL1Object,
    deactiveL1Object
  };
};

const getBudgetValue = (budget: any, currency: string) => {
  const hasBudget = budget.lifetime !== -1;
  const hasDailyBudget = budget.daily !== -1;
  if (hasBudget) {
    return formatPrice(currency, +budget.lifetime);
  }
  if (hasDailyBudget) {
    return i18n.t('common.valuePerDay', {
      value: formatPrice(currency, +budget.daily)
    });
  }
  return i18n.t('common.labels.noData');
};

const getFilterTypeDes = (type: L1ObjectListColumns, l1Object, order) => {
  let data: any;
  switch (type) {
    case L1ObjectListColumns.DELIVERY:
      data = getRtbCampaignGroupDeliveryDesDatas(l1Object);
      return _.map(data, 'des');
    case L1ObjectListColumns.STATE:
    default:
      data = getRtbCampaignGroupStatusDesData(l1Object, order);
      return data.des;
  }
};

const basicSorter = (dataA, dataB, order) => {
  if (order === 'desc') {
    return dataA > dataB ? -1 : 1;
  } else {
    return dataA < dataB ? -1 : 1;
  }
};

const budgetSorter = (dataA, dataB, order) => {
  const dataADaily = _.get(dataA, 'daily');
  const dataBDaily = _.get(dataB, 'daily');
  const dataALifetime = _.get(dataA, 'lifetime');
  const dataBLifetime = _.get(dataB, 'lifetime');
  const allDailyType = dataADaily !== -1 && dataBDaily !== -1;
  const allLifetimeType = dataALifetime !== -1 && dataBLifetime !== -1;
  const allNoBudget =
    dataADaily === -1 &&
    dataBDaily === -1 &&
    dataALifetime === -1 &&
    dataBLifetime === -1;
  if (allNoBudget) {
    return 0;
  }
  if (allDailyType) {
    return basicSorter(dataADaily, dataBDaily, order);
  }
  if (allLifetimeType) {
    basicSorter(dataALifetime, dataBLifetime, order);
  }

  return dataALifetime !== -1 ? -1 : 1;
};

const getL1ObjectProgress = (l1Object: L1Object) => {
  const l2ObjectRunningStatus = l1Object.l2ObjectRunningStatus;

  const budget = _.get(
    l2ObjectRunningStatus,
    L2ObjectRunningStatus.BUDGET,
    0
  ) as number;
  const spent = _.get(
    l2ObjectRunningStatus,
    L2ObjectRunningStatus.SPENT,
    0
  ) as number;
  const executeRate = _.get(
    l2ObjectRunningStatus,
    L2ObjectRunningStatus.ACTUAL_PROGRESS,
    0
  ) as number;
  const predictRate = _.get(
    l2ObjectRunningStatus,
    L2ObjectRunningStatus.EXPECTED_PROGRESS,
    0
  ) as number;
  const discrepancy = predictRate - executeRate;

  return {
    executeRate,
    predictRate,
    danger: spent > budget * 1.1,
    deepWarning: discrepancy > 0.03,
    warning: discrepancy > 0.01 && discrepancy <= 0.03
  };
};

const getCanNotCreateMessage = (order, allowedChannels) => {
  const canNotCreateState = [
    State.NOT_APPROVE,
    State.REJECT,
    State.SETTLE,
    State.SETTLED,
    State.CHANGE_PENDING
  ];
  const state = order.state;
  if (canNotCreateState.includes(state)) {
    return i18n.t('l1ObjectList.labels.orderStateCannotCreate');
  }

  const isEnd = moment(order.endDate).isBefore(moment().startOf('day'));
  if (isEnd) {
    return i18n.t('l1ObjectList.labels.isEndCannotCreate');
  }

  if (_.isEmpty(allowedChannels)) {
    return i18n.t('l1ObjectList.labels.noChannelAllowed');
  }

  return '';
};

const l1ObjectListFilter = (
  l1Object,
  searchString: string,
  selectedChannel: string,
  order: Order,
  selectedStatusFilter: string[],
  selectedDeliveryFilter: string[]
) => {
  const nameInclude = l1Object.name
    .toLowerCase()
    .includes(searchString.toLowerCase());
  const idInclude = l1Object.l1ObjectId.toString().includes(searchString);
  const channelCorrect =
    selectedChannel === ListChannel.ALL || l1Object.channel === selectedChannel;
  const filterInclude =
    (selectedStatusFilter.length === 0 &&
      selectedDeliveryFilter.length === 0 &&
      l1Object.state !== L1ObjectState.DELETE) ||
    selectedStatusFilter.includes(
      getFilterTypeDes(L1ObjectListColumns.STATE, l1Object, order)
    ) ||
    !_.isEmpty(
      _.intersection(
        selectedDeliveryFilter,
        getFilterTypeDes(L1ObjectListColumns.DELIVERY, l1Object, order)
      )
    );
  return channelCorrect && (nameInclude || idInclude) && filterInclude;
};

const handleOnSelect = (selectedArray, objectId) => {
  if (selectedArray.indexOf(objectId.toString()) > -1) {
    return selectedArray.filter((id) => id.toString() !== objectId.toString());
  }
  return [...selectedArray, objectId.toString()];
};

const handleOnSelectAll = (selectedArrays, editalbeArrays) => {
  if (selectedArrays.length === editalbeArrays.length) {
    return [];
  }

  return editalbeArrays.map((l1Object) => l1Object.l1ObjectId.toString());
};
